/**
 * Ready-made computed columns the picker offers beside the server's own
 * (owner, 2026-10-26: "add ATH column").
 *
 * A computed column lives only in `ScreenerConfig`, so the picker never lists
 * one until somebody writes it. These are written for them: ticking one in the
 * picker adds the definition below, after which it is an ordinary computed
 * column of that screen — editable, renamable, deletable in the editor.
 *
 * `ath` reads the bar writer's whole-history column (`ohlc/local.py`), because
 * the screener's frame starts in 2020; see `expr_polars._fn_ath`.
 */

import type { ScreenerColumn, ScreenerExpr } from "@qufolio/model";

export interface SuggestedColumn {
  /** The column definition the tick adds. Always computed. */
  column: ScreenerColumn & { computed: NonNullable<ScreenerColumn["computed"]> };
  description: string;
}

const ath: ScreenerExpr = { kind: "call", fn: "ath", args: [{ kind: "field", field: "high" }] };
const close: ScreenerExpr = { kind: "field", field: "close" };

export const SUGGESTED_COLUMNS: readonly SuggestedColumn[] = [
  {
    column: {
      field: "ath",
      header: "ATH",
      computed: { expression: ath, source: "ath(high)", requires: ["high"], rollup: "none" },
    },
    description: "All-time high: the highest high in the symbol's whole history",
  },
  {
    column: {
      field: "pctFromAth",
      header: "% from ATH",
      precision: 1,
      computed: {
        expression: {
          kind: "binary",
          op: "*",
          left: {
            kind: "binary",
            op: "/",
            left: { kind: "binary", op: "-", left: close, right: ath },
            right: ath,
          },
          right: { kind: "literal", value: 100 },
        },
        source: "(close - ath(high)) / ath(high) * 100",
        requires: ["close", "high"],
        rollup: "none",
      },
    },
    description: "How far the close is below its all-time high, in percent (0 at a new high)",
  },
];

export const SUGGESTED_BY_FIELD: ReadonlyMap<string, SuggestedColumn> = new Map(
  SUGGESTED_COLUMNS.map((s) => [s.column.field, s]),
);
